import { AdminType, APIDelete, APIGet, APIPost, APIPut, APIUrl, createFormData } from "api/protocols";
import { CancelToken } from "axios";
import { IWebsiteBuilderWebsite, IWebsiteBuilderFont, IWebsiteBuilderWebsiteVersionGlobalStyles, IWebsiteBuilderMediaItem, IWebsiteBuilderTemplate, IWebsiteBuilderTheme, IWebsiteBuilderWebsiteVersion, IWebsiteBuilderWebsiteVersionPageRaw, IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw, TWebsiteBuilderWebsiteVersionNavigationMenuItemType, IWebsiteBuilderFormRaw, IWebsiteBuilderFormSubmission, IWebsiteBuilderFormRowRaw, IWebsiteBuilderFormSummary, IWebsiteBuilderIconAsset, IWebsiteBuilderIconProvider, IWebsiteBuilderIconAssetIdentityRaw } from "redux/reducers/models/websiteBuilder";

interface IGetWebsiteBuilderWebsite {
  facility_id: number;
}

interface IGetWebsiteBuilderWebsiteRes {
  website: IWebsiteBuilderWebsite;
}

interface IPostWebsiteBuilderWebsite {
  template_id: number;
}

interface IUpdateWebsiteBuilderWebsite {
  id: number;
  domain?: string;
  favicon?: File;
  logo?: File;
}

interface IDeleteWebsiteBuilderWebsite {
  id: number;
}

interface IDeleteWebsiteBuilderWebsiteImage {
  id: number;
  image_type: "logo" | "favicon";
}

interface IUnpublishWebsiteBuilderWebsite {
  id: number;
}

interface IReapplyWebsiteBuilderTemplate {
  website_id: number;
  template_id: number;
  preserve_content: boolean;
}

interface IGetWebsiteBuilderWebsiteVersion {
  id: number;
}

interface IGetWebsiteBuilderWebsiteVersions {
  website_id: number;
}

interface IPutWebsiteBuilderWebsiteVersion {
  id: number;
  header?: string;
  footer?: string;
  theme_id?: number;
}

export interface IPublishWebsiteBuilderWebsiteVersionPage {
  id: number;
  slug: string;
  is_home: boolean;
  root: string;
  html: string;
}

interface IPublishWebsiteBuilderWebsiteVersion {
  website_version_id: number;
  css: File;
  js: File;
  header: string;
  footer: string;
  pages: IPublishWebsiteBuilderWebsiteVersionPage[];
}

interface IPublishWebsiteBuilderWebsiteVersionRes {
  website: IWebsiteBuilderWebsite;
  new_version: IWebsiteBuilderWebsiteVersion;
  global_styles: IWebsiteBuilderWebsiteVersionGlobalStyles;
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
}

export interface IRestoreWebsiteBuilderWebsiteVersionPage {
  title: string;
  slug: string;
  is_home: boolean;
  root: string;
}

interface IRestoreWebsiteBuilderWebsiteVersion {
  version_id: number;
  header: string;
  footer: string;
  pages: IRestoreWebsiteBuilderWebsiteVersionPage[];
}

interface IRestoreWebsiteBuilderWebsiteVersionRes {
  website: IWebsiteBuilderWebsite;
  new_version: IWebsiteBuilderWebsiteVersion;
  global_styles: IWebsiteBuilderWebsiteVersionGlobalStyles;
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
}

interface IGetWebsiteBuilderWebsiteVersionPages {
  website_version_id: number;
}

interface IPostWebsiteBuilderWebsiteVersionPage {
  website_version_id: number;
  title: string;
  slug: string;
  is_home: boolean;
  root: string;
  parent_id: number;
}

interface IPostWebsiteBuilderWebsiteVersionPageRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
  page: IWebsiteBuilderWebsiteVersionPageRaw;
}

interface IDeleteWebsiteBuilderWebsiteVersionPage {
  id: number;
}

interface IDeleteWebsiteBuilderWebsiteVersionPageRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
}

interface IPutWebsiteBuilderWebsiteVersionPage {
  id: number;
  title?: string;
  slug?: string;
  is_home?: boolean;
  root?: string;
  position?: number;
}

interface IPutWebsiteBuilderWebsiteVersionPageHome {
  id: number;
}

interface IPutWebsiteBuilderWebsiteVersionPageHomeRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
}

interface IGetWebsiteBuilderWebsiteVersionNavigationMenuItems {
  website_version_id: number;
}

export interface IPutWebsiteBuilderWebsiteVersionNavigationMenuItem {
  id: number;
  page_id?: number;
  type: string;
  label?: string;
  external_url?: string;
  open_in_new_tab: boolean;
  position: number;
  parent_id?: number;
  hidden: boolean;
}

export type TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdate = Pick<IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw, "id" | "position" | "parent_id">;
export type TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates = TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdate[];

interface IPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositions {
  items: TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates;
}

interface IPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionsRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
}

export interface IPostWebsiteBuilderWebsiteVersionNavigationMenuItem {
  website_version_id: number;
  page_id?: number;
  type: TWebsiteBuilderWebsiteVersionNavigationMenuItemType;
  label?: string;
  external_url?: string;
  open_in_new_tab: boolean;
  parent_id: number;
}

interface IPostWebsiteBuilderWebsiteVersionNavigationMenuItemRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
}

interface IDeleteWebsiteBuilderWebsiteVersionNavigationMenuItem {
  id: number;
}

interface IDeleteWebsiteBuilderWebsiteVersionNavigationMenuItemRes {
  menu_items: IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[];
  pages: IWebsiteBuilderWebsiteVersionPageRaw[];
}

interface IGetWebsiteBuilderTemplate {
  id: number;
}

interface IRetrieveWebsiteBuilderIconAssetsByIdentities {
  identities: IWebsiteBuilderIconAssetIdentityRaw[];
}

interface IGetWebsiteBuilderIconAssets {
  provider_code: string;
  version_code: string;
  variant_code: string;
}

interface IGetWebsiteBuilderWebsiteVersionGlobalStyles {
  website_version_id: number;
}

interface IPutWebsiteBuilderWebsiteVersionGlobalStyles {
  website_version_id: number;
  heading_color: string;
  heading_font_id: number;
  body_text_color: string;
  body_text_font_id: number;
  body_text_font_size: number;
  button_color: string;
  button_background_color: string;
  button_border_color: string;
  button_hover_color: string;
  button_hover_background_color: string;
  button_hover_border_color: string;
  button_hover_transition: string;
  link_color: string;
  link_hover_color: string;
}

interface IGetWebsiteBuilderMedia {
  website_id: number;
}

interface IPostWebsiteBuilderMediaItem {
  website_id: number;
  file: File;
}

interface IArchiveWebsiteBuilderMediaItem {
  id: number;
}

interface IRestoreWebsiteBuilderMediaItem {
  id: number;
}

interface IGetWebsiteBuilderForms {
  website_id: number;
}

interface IPostWebsiteBuilderForm {
  key: string;
  website_id: number;
  name: string;
  email_notification_enabled: boolean;
  rows: IWebsiteBuilderFormRowRaw[];
}

interface IPutWebsiteBuilderForm {
  id: number;
  name?: string;
  email_notification_enabled?: boolean;
  rows?: IWebsiteBuilderFormRowRaw[];
}

interface IArchiveWebsiteBuilderForm {
  id: number;
}

interface IGetWebsiteBuilderFormSummaries {
  website_id: number;
  offset?: number;
  limit?: number;
  search?: string;
}

interface IGetWebsiteBuilderFormSummary {
  website_id: number;
  form_id: number;
}

interface IGetWebsiteBuilderFormSubmissions {
  form_id: number;
  offset: number;
  limit: number;
}

export function GetWebsiteBuilderWebsite(params: IGetWebsiteBuilderWebsite, useGlobalLoader: boolean) {
  return APIGet<IGetWebsiteBuilderWebsiteRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website"), params, useGlobalLoader);
}

export function PostWebsiteBuilderWebsite(params: IPostWebsiteBuilderWebsite, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderWebsite>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website"), params, useGlobalLoader);
}

export function UpdateWebsiteBuilderWebsite(params: IUpdateWebsiteBuilderWebsite, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderWebsite>(
    APIUrl(AdminType.FacilityAdmin, "/website-builder/website/update"),
    createFormData(params),
    useGlobalLoader,
  );
}

export function DeleteWebsiteBuilderWebsite(params: IDeleteWebsiteBuilderWebsite, useGlobalLoader: boolean) {
  return APIDelete<IWebsiteBuilderWebsite>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website"), params, useGlobalLoader);
}

export function DeleteWebsiteBuilderWebsiteImage(params: IDeleteWebsiteBuilderWebsiteImage, useGlobalLoader: boolean) {
  return APIDelete<IWebsiteBuilderWebsite>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/image"), params, useGlobalLoader);
}

export function UnpublishWebsiteBuilderWebsite(params: IUnpublishWebsiteBuilderWebsite, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderWebsite>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/unpublish"), params, useGlobalLoader);
}

export function ReapplyWebsiteBuilderTemplate(params: IReapplyWebsiteBuilderTemplate, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderWebsite>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/reapply-template"), params, useGlobalLoader);
}

export function GetWebsiteBuilderWebsiteVersion(
  params: IGetWebsiteBuilderWebsiteVersion,
  useGlobalLoader: boolean,
  token: CancelToken,
) {
  return APIGet<IWebsiteBuilderWebsiteVersion>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderWebsiteVersions(
  params: IGetWebsiteBuilderWebsiteVersions,
  useGlobalLoader: boolean,
  token: CancelToken,
) {
  return APIGet<IWebsiteBuilderWebsiteVersion[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/versions"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersion(params: IPutWebsiteBuilderWebsiteVersion, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<unknown>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version"), params, useGlobalLoader, token);
}

export function PublishWebsiteBuilderWebsiteVersion(params: IPublishWebsiteBuilderWebsiteVersion, useGlobalLoader: boolean) {
  const formData = new FormData();
  
  formData.append("website_version_id", String(params.website_version_id));
  formData.append("css", params.css);
  formData.append("js", params.js);
  formData.append("header", params.header);
  formData.append("footer", params.footer);

  params.pages.forEach((page, i) => {
    const namePrefix = `pages[${i}]`;

    formData.append(`${namePrefix}[id]`, String(page.id));
    formData.append(`${namePrefix}[slug]`, page.slug);
    formData.append(`${namePrefix}[is_home]`, page.is_home ? "1" : "0");
    formData.append(`${namePrefix}[root]`, page.root);
    formData.append(`${namePrefix}[html]`, page.html);
  });
  
  return APIPost<IPublishWebsiteBuilderWebsiteVersionRes>(
    APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/publish"),
    formData,
    useGlobalLoader,
  );
}

export function RestoreWebsiteBuilderWebsiteVersion(params: IRestoreWebsiteBuilderWebsiteVersion, useGlobalLoader: boolean) {
  return APIPost<IRestoreWebsiteBuilderWebsiteVersionRes>(
    APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/restore"),
    params,
    useGlobalLoader,
  );
}

export function GetWebsiteBuilderWebsiteVersionPages(params: IGetWebsiteBuilderWebsiteVersionPages, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderWebsiteVersionPageRaw[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/pages"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersionPage(params: IPutWebsiteBuilderWebsiteVersionPage, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IWebsiteBuilderWebsiteVersionPageRaw>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/page"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersionPageHome(params: IPutWebsiteBuilderWebsiteVersionPageHome, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IPutWebsiteBuilderWebsiteVersionPageHomeRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/page/set-home"), params, useGlobalLoader, token);
}

export function PostWebsiteBuilderWebsiteVersionPage(params: IPostWebsiteBuilderWebsiteVersionPage, useGlobalLoader: boolean) {
  return APIPost<IPostWebsiteBuilderWebsiteVersionPageRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/page"), params, useGlobalLoader);
}

export function DeleteWebsiteBuilderWebsiteVersionPage(params: IDeleteWebsiteBuilderWebsiteVersionPage, useGlobalLoader: boolean) {
  return APIDelete<IDeleteWebsiteBuilderWebsiteVersionPageRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/page"), params, useGlobalLoader);
}

export function GetWebsiteBuilderWebsiteVersionNavigationMenuItems(params: IGetWebsiteBuilderWebsiteVersionNavigationMenuItems, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/navigation-menu-items"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersionNavigationMenuItem(params: IPutWebsiteBuilderWebsiteVersionNavigationMenuItem, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IWebsiteBuilderWebsiteVersionNavigationMenuItemRaw>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/navigation-menu-item"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersionNavigationMenuItemPositions(params: IPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositions, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionsRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/navigation-menu-items/bulk-update-positions"), params, useGlobalLoader, token);
}

export function PostWebsiteBuilderWebsiteVersionNavigationMenuItem(params: IPostWebsiteBuilderWebsiteVersionNavigationMenuItem, useGlobalLoader: boolean) {
  return APIPost<IPostWebsiteBuilderWebsiteVersionNavigationMenuItemRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/navigation-menu-item"), params, useGlobalLoader);
}

export function DeleteWebsiteBuilderWebsiteVersionNavigationMenuItem(params: IDeleteWebsiteBuilderWebsiteVersionNavigationMenuItem, useGlobalLoader: boolean) {
  return APIDelete<IDeleteWebsiteBuilderWebsiteVersionNavigationMenuItemRes>(APIUrl(AdminType.FacilityAdmin, "/website-builder/website/version/navigation-menu-item"), params, useGlobalLoader);
}

export function GetWebsiteBuilderTemplates(useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderTemplate[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/templates"), null, useGlobalLoader, token);
}

export function GetWebsiteBuilderTemplate(params: IGetWebsiteBuilderTemplate, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderTemplate>(APIUrl(AdminType.FacilityAdmin, "/website-builder/template"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderThemes(useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderTheme[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/themes"), null, useGlobalLoader, token);
}

export function GetWebsiteBuilderFonts(useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderFont[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/fonts"), null, useGlobalLoader, token);
}

export function RetrieveWebsiteBuilderIconAssetsByIdentities(params: IRetrieveWebsiteBuilderIconAssetsByIdentities, useGlobalLoader: boolean, token: CancelToken) {
  return APIPost<IWebsiteBuilderIconAsset[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/icon-assets/identities"), { identities: JSON.stringify(params.identities) }, useGlobalLoader, token);
}

export function GetWebsiteBuilderIconProviders(useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderIconProvider[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/icons"), null, useGlobalLoader, token);
}

export function GetWebsiteBuilderIconAssets(params: IGetWebsiteBuilderIconAssets, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderIconAsset[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/icon-assets"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderWebsiteVersionGlobalStyles(params: IGetWebsiteBuilderWebsiteVersionGlobalStyles, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderWebsiteVersionGlobalStyles>(APIUrl(AdminType.FacilityAdmin, "/website-builder/global-styles"), params, useGlobalLoader, token);
}

export function PutWebsiteBuilderWebsiteVersionGlobalStyles(params: IPutWebsiteBuilderWebsiteVersionGlobalStyles, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IWebsiteBuilderWebsiteVersionGlobalStyles>(APIUrl(AdminType.FacilityAdmin, "/website-builder/global-styles"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderMedia(params: IGetWebsiteBuilderMedia, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/media"), params, useGlobalLoader, token);
}

export function PostWebsiteBuilderMediaItem(params: IPostWebsiteBuilderMediaItem, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderMediaItem[]>(
    APIUrl(AdminType.FacilityAdmin, "/website-builder/media"),
    createFormData(params),
    useGlobalLoader,
  );
}

export function ArchiveWebsiteBuilderMediaItem(params: IArchiveWebsiteBuilderMediaItem, useGlobalLoader: boolean) {
  return APIPut<IWebsiteBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/media/archive"), params, useGlobalLoader);
}

export function RestoreWebsiteBuilderMediaItem(params: IRestoreWebsiteBuilderMediaItem, useGlobalLoader: boolean) {
  return APIPut<IWebsiteBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/media/restore"), params, useGlobalLoader);
}

export function GetWebsiteBuilderStockMedia(useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/media/stock"), null, useGlobalLoader, token);
}

export function GetWebsiteBuilderForms(params: IGetWebsiteBuilderForms, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderFormRaw[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form"), params, useGlobalLoader, token);
}

export function PostWebsiteBuilderForm(params: IPostWebsiteBuilderForm, useGlobalLoader: boolean) {
  return APIPost<IWebsiteBuilderFormRaw>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form"), params, useGlobalLoader);
}

export function PutWebsiteBuilderForm(params: IPutWebsiteBuilderForm, useGlobalLoader: boolean, token: CancelToken) {
  return APIPut<IWebsiteBuilderFormRaw>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form"), params, useGlobalLoader, token);
}

export function ArchiveWebsiteBuilderForm(params: IArchiveWebsiteBuilderForm, useGlobalLoader: boolean) {
  return APIDelete<IWebsiteBuilderFormRaw>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form"), params, useGlobalLoader);
}

export function GetWebsiteBuilderFormSummaries(params: IGetWebsiteBuilderFormSummaries, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderFormSummary[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form/summary"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderFormSummary(params: IGetWebsiteBuilderFormSummary, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderFormSummary[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form/summary"), params, useGlobalLoader, token);
}

export function GetWebsiteBuilderFormSubmissions(params: IGetWebsiteBuilderFormSubmissions, useGlobalLoader: boolean, token: CancelToken) {
  return APIGet<IWebsiteBuilderFormSubmission[]>(APIUrl(AdminType.FacilityAdmin, "/website-builder/form/submission"), params, useGlobalLoader, token);
}